import { useState, useRef, useEffect } from "react";
import { QueryKey, useQueryClient } from "@tanstack/react-query";
import { useRouter, useSearchParams } from "next/navigation";

import { useAppLayerContext } from "@/shared/contexts/AppLayer";

import { CookieUtils } from "@/shared/utils/CookieUtils";
import { UIUtils } from "@/shared/utils/UIUtils";
import { PublicConfig } from "@/shared/PublicConfig";
import { Quote } from "@/shared/types/Quote.interface";
import Strings from "../utils/Strings.constants";
import { AnalyticsUtils } from "../utils/AnalyticsUtils";

type UseQuoteStatusProps = {
    quote?: Quote;
    isSubmitting: boolean;
    cookieQuoteId: string;
    cookieFinalizeStarted: string;
    underwriter: string;
    formId: string;
    /**
     * When true, the form is rendered inside a parent iframe that's responsible for
     * tearing down the iframe on success. Skips the default thank-you redirect so
     * the iframe doesn't navigate before the parent removes it. The host form is
     * expected to handle its own success UX (e.g. window.parent.postMessage).
     */
    isEmbedded?: boolean;
    /**
     * Query key the polling tick should invalidate to refetch the quote.
     * Defaults to `["quote"]` (matches `useQuote`/ptz-us).
     */
    pollingQueryKey?: QueryKey;
};
const DEFAULT_POLLING_QUERY_KEY: QueryKey = ["quote"];

export const useQuoteStatus = ({
    quote,
    isSubmitting,
    underwriter,
    cookieQuoteId,
    cookieFinalizeStarted,
    formId,
    isEmbedded = false,
    pollingQueryKey = DEFAULT_POLLING_QUERY_KEY
}: UseQuoteStatusProps) => {
    const [shouldPollStatus, setShouldPollStatus] = useState(false);
    const [hasTimedOut, setHasTimedOut] = useState(false);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const queryClient = useQueryClient();
    const { appState, updateAppState } = useAppLayerContext();
    const router = useRouter();
    const searchParams = useSearchParams();

    const { quoteStatus } = quote || {};

    const isFinalizeCookieSet = CookieUtils.exists(cookieFinalizeStarted);
    const isStatusOpenWithFinalizeCookie = quoteStatus === "open" && isFinalizeCookieSet;

    useEffect(() => {
        if (quoteStatus === "failed-authorization" && isFinalizeCookieSet && !isSubmitting) {
            // Remove finalize cookie to prevent polling restart on reload
            CookieUtils.remove(cookieFinalizeStarted);

            AnalyticsUtils.sendDataDogLog("error", "PAYMENT - Finalize quote status: failed-authorization", {
                quoteId: quote?.id
            });

            updateAppState({
                isQuoteUpdating: false,
                showFinalLoader: false,
                stripePaymentElementError: {
                    stripe: true,
                    header: appState.isPrepaidCard ? Strings.ERRORS.DECLINE_PREPAID : Strings.ERRORS.DECLINE_GENERIC,
                    message: "failed-authorization",
                    description: appState.isPrepaidCard ? Strings.ERRORS.DECLINE_PREPAID_DESCRIPTION : undefined
                }
            });
        }
    }, [quoteStatus, updateAppState, cookieFinalizeStarted, quote?.id, isFinalizeCookieSet, isSubmitting, appState.isPrepaidCard]);

    useEffect(() => {
        const scrollToTop = async () => {
            await UIUtils.scrollToTop(3000, 50);
        };

        // Don't start polling if we've already timed out
        if (hasTimedOut) {
            return;
        }

        if ((quoteStatus === "finalize-processing" || isStatusOpenWithFinalizeCookie) && !pollRef.current) {
            updateAppState({ isQuoteUpdating: true, showFinalLoader: true });
            setShouldPollStatus(true);
            scrollToTop();

            pollRef.current = setInterval(() => {
                queryClient.invalidateQueries({ queryKey: pollingQueryKey });
            }, PublicConfig.PTZ_US.FINALIZE_POLLING_INTERVAL);

            timeoutRef.current = setTimeout(() => {
                if (pollRef.current) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                }
                setShouldPollStatus(false);
                setHasTimedOut(true);

                if (isStatusOpenWithFinalizeCookie) {
                    CookieUtils.remove(cookieFinalizeStarted);
                    window.location.reload();
                } else {
                    updateAppState({
                        isQuoteUpdating: false,
                        showFinalLoader: false,
                        isEnrollmentTimeoutModalVisible: true
                    });
                }
            }, PublicConfig.PTZ_US.FINALIZE_MAX_TIMEOUT);
        }

        if (shouldPollStatus && pollRef.current && quoteStatus !== "finalize-processing" && !isStatusOpenWithFinalizeCookie) {
            setShouldPollStatus(false);
            clearInterval(pollRef.current);
            pollRef.current = null;

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            updateAppState({ isQuoteUpdating: false, showFinalLoader: false });
        }

        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [cookieFinalizeStarted, hasTimedOut, isStatusOpenWithFinalizeCookie, pollingQueryKey, queryClient, quoteStatus, shouldPollStatus, updateAppState]);

    useEffect(() => {
        const scrollToTop = async () => {
            await UIUtils.scrollToTop(3000, 50);
        };

        if (quoteStatus === "finalized" && !isSubmitting) {
            scrollToTop();
            CookieUtils.remove(cookieQuoteId);
            // Embedded forms (e.g. add-a-pet inside Member Center) opt out of the redirect;
            // their host signals success via postMessage and tears down the iframe itself.
            if (isEmbedded) return;
            const params = new URLSearchParams(searchParams.toString());
            const _quoteIdInParams = UIUtils.getCaseInsensitiveValue(params, "quoteId");
            const _underwriter = UIUtils.getCaseInsensitiveValue(params, "uw");
            const _quoteId = quote?.id;

            if (_quoteId && !_quoteIdInParams) {
                params.set("quoteId", _quoteId);
            }

            if (!_underwriter) {
                params.set("uw", underwriter);
            }
            const newUrl = UIUtils.toUrlString(`${PublicConfig.BASE_PATH}/forms/${formId}/thankyou`, params);
            router.push(newUrl);
        }
    }, [cookieQuoteId, formId, isEmbedded, isSubmitting, quote?.id, quoteStatus, router, searchParams, underwriter]);
};
